import promotionService from "@/services/promotion-service";
import indexDbService from "@/helpers/dbHelper";

const state = () => ({
  promotions: [],
  loading: false,
  error: null,
  lastFetched: null
});

const getters = {
  allPromotions: (state) => state.promotions,
  activePromotions: (state) => {
    const active = state.promotions.filter(promotion => {
      const isActive = promotionService.isPromotionActive(promotion);
      return isActive;
    });
    return active;
  },
  isLoading: (state) => state.loading,
  error: (state) => state.error,
  isStale: (state) => {
    if (!state.lastFetched) return true;
    const now = new Date();
    const lastFetched = new Date(state.lastFetched);
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    return (now - lastFetched) > fiveMinutes;
  }
};

const actions = {
  async fetchPromotions({ commit, state }) {
    // Don't fetch if already loading
    if (state.loading) {
      return state.promotions;
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Store all active promotion channels locally. The activePromotions getter
      // still exposes POS-applicable promotions by default, while online orders
      // can re-evaluate against online_order/all rules when opened in WebPOS.
      const promotions = await promotionService.getActivePromotions();
      // Save promotions to IndexedDB for offline persistence
      await Promise.all(promotions.map(promotion => indexDbService.saveToDb('promotions', promotion)));
      commit('SET_PROMOTIONS', promotions);
      commit('SET_LAST_FETCHED', new Date().toISOString());
      return promotions;
    } catch (error) {
      commit('SET_ERROR', error.message);
      console.error('Error fetching promotions:', error);
      return [];
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async refreshPromotions({ dispatch }) {
    return await dispatch('fetchPromotions');
  },

  clearPromotions({ commit }) {
    commit('SET_PROMOTIONS', []);
    commit('SET_LAST_FETCHED', null);
  },

  getApplicablePromotions({ state }, product) {
    return promotionService.getApplicablePromotions(product, state.promotions);
  },

  getApplicableOrderPromotions({ state }, currentOrder) {
    return promotionService.getApplicableOrderPromotions(currentOrder, state.promotions);
  },

  async fetchPromotionsFromDb({ commit }) {
    try {
      const promotions = await indexDbService.getDbData('promotions');
      commit('SET_PROMOTIONS', promotions);
      return promotions;
    } catch (error) {
      console.error('Error fetching promotions from IndexedDB:', error);
      return [];
    }
  }
};

const mutations = {
  SET_PROMOTIONS(state, promotions) {
    state.promotions = promotions;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_LAST_FETCHED(state, timestamp) {
    state.lastFetched = timestamp;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
